import React, { useState } from 'react'
import { GitFork, SlidersHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ContextMenu, ContextMenuRowButton } from '@/components/ui/ContextMenu'
import { MaterialPropertiesPanel } from '@/features/materials/MaterialPropertiesPanel'
import type { MrpPlanningRow } from '../types/mrpTypes'
import { MrpSpecBadge } from './MrpSpecBadge'
import { MrpIssueBadge } from './MrpIssueBadge'
import { MrpSuggestionSourceBadge } from './MrpSuggestionSourceBadge'
import { formatNumberVi } from '../utils/mrpFormatters'
import { buildMrpRowKey } from '../utils/mrpRowIdentity'

export interface MrpPlanningTableProps {
  rows: MrpPlanningRow[]
  onOpenTrace: (row: MrpPlanningRow) => void
}

export function MrpPlanningTable({
  rows,
  onOpenTrace,
}: MrpPlanningTableProps): React.ReactElement {
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null)
  const [isPropsPanelOpen, setIsPropsPanelOpen] = useState(false)
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean
    x: number
    y: number
    row: MrpPlanningRow | null
  }>({
    isOpen: false,
    x: 0,
    y: 0,
    row: null,
  })

  return (
    <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
      <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
        <thead className="bg-slate-50 font-semibold text-slate-700">
          <tr>
            <th className="px-3 py-3 w-12 text-center">STT</th>
            <th className="px-3 py-3 min-w-[120px]">Mã vật tư</th>
            <th className="px-3 py-3 min-w-[220px]">Tên vật tư</th>
            <th className="px-3 py-3 min-w-[160px]">Quy cách</th>
            <th className="px-2 py-3 w-16 text-center">ĐVT</th>
            <th className="px-3 py-3 text-right">Tổng nhu cầu</th>
            <th className="px-3 py-3 text-right text-slate-600">Đã giữ hàng</th>
            <th className="px-3 py-3 text-right text-emerald-800">Tồn tự do</th>
            <th className="px-3 py-3 text-right text-indigo-700">PO đang về</th>
            <th className="px-3 py-3 text-right font-bold text-rose-700 bg-rose-50/50">
              Lượng thiếu
            </th>
            <th className="px-3 py-3 text-right font-semibold text-slate-800">
              Đề xuất mua
            </th>
            <th className="px-3 py-3 text-center min-w-[120px]">Nguồn gợi ý</th>
            <th className="px-3 py-3 min-w-[160px]">Cảnh báo</th>
            <th className="px-3 py-3 w-24 text-center">Nhu cầu</th>
            <th className="px-3 py-3 w-24 text-center">Thuộc tính</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {rows.map((row, idx) => {
            const rowKey = buildMrpRowKey({
              row_id: row.row_id,
              material_id: row.material_id,
              uom: row.uom,
              material_spec: row.material_spec,
            })
            const isShortage = row.shortage_qty > 0

            return (
              <tr
                key={rowKey}
                data-testid={`mrp-row-${rowKey}`}
                className={`hover:bg-slate-50/70 transition-colors ${
                  isShortage ? 'bg-rose-50/20' : ''
                }`}
                onContextMenu={(e) => {
                  if (row.material_id) {
                    e.preventDefault()
                    setContextMenu({
                      isOpen: true,
                      x: e.clientX,
                      y: e.clientY,
                      row,
                    })
                  }
                }}
              >
                {/* STT */}
                <td className="px-3 py-2.5 text-center text-slate-400 tabular-nums">
                  {idx + 1}
                </td>

                {/* Mã vật tư */}
                <td className="px-3 py-2.5 font-mono text-slate-700 font-medium text-[11px] whitespace-nowrap">
                  {row.material_code || '—'}
                </td>

                {/* Tên vật tư */}
                <td className="px-3 py-2.5">
                  <div className="font-semibold text-slate-900 leading-snug">
                    {row.material_name || (
                      <span className="italic text-rose-500">Chưa xác định tên vật tư</span>
                    )}
                  </div>
                </td>

                {/* Quy cách / structured material spec */}
                <td className="px-3 py-2.5">
                  <MrpSpecBadge spec={row.material_spec} />
                </td>

                {/* ĐVT */}
                <td className="px-2 py-2.5 text-center font-medium text-slate-700 uppercase">
                  {row.uom || '—'}
                </td>

                {/* Tổng nhu cầu (server authoritative) */}
                <td className="px-3 py-2.5 text-right tabular-nums font-medium text-slate-900">
                  {formatNumberVi(row.required_qty)}
                </td>

                {/* Đã giữ hàng (server authoritative) */}
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">
                  {formatNumberVi(row.reserved_qty)}
                </td>

                {/* Tồn tự do (server authoritative) */}
                <td className="px-3 py-2.5 text-right tabular-nums font-medium text-emerald-800">
                  {formatNumberVi(row.free_unreserved_qty)}
                </td>

                {/* PO đang về (server authoritative) */}
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {row.incoming_po_qty > 0 ? (
                    <span
                      className="inline-block rounded bg-indigo-50 px-1.5 py-0.5 font-semibold text-indigo-700 border border-indigo-200"
                      title="Đang trên đường về từ các đơn PO mở"
                    >
                      {`+${formatNumberVi(row.incoming_po_qty)}`}
                    </span>
                  ) : (
                    <span className="text-slate-400">0</span>
                  )}
                </td>

                {/* Thiếu (server authoritative — highlighted if > 0) */}
                <td
                  className={`px-3 py-2.5 text-right tabular-nums ${
                    isShortage
                      ? 'bg-rose-50/70 font-bold text-rose-700'
                      : 'text-slate-400'
                  }`}
                  data-testid="mrp-shortage-cell"
                >
                  {isShortage ? (
                    <span className="inline-block rounded bg-rose-100 px-2 py-0.5 font-bold text-rose-800 border border-rose-300">
                      {formatNumberVi(row.shortage_qty)}
                    </span>
                  ) : (
                    '0'
                  )}
                </td>

                {/* Đề xuất mua (server authoritative) */}
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900">
                  {row.recommended_buy_qty > 0 ? (
                    <span className="text-blue-700">
                      {formatNumberVi(row.recommended_buy_qty)}
                    </span>
                  ) : (
                    <span className="text-slate-400">0</span>
                  )}
                </td>

                {/* Nguồn gợi ý (MRP vs POLICY) */}
                <td className="px-3 py-2.5 text-center">
                  <MrpSuggestionSourceBadge source={row.suggestion_source} />
                </td>

                {/* Cảnh báo / issues */}
                <td className="px-3 py-2.5">
                  <MrpIssueBadge issues={row.issues} />
                </td>

                {/* Nguồn nhu cầu (traceability trigger) */}
                <td className="px-3 py-2.5 text-center">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onOpenTrace(row)}
                    className="gap-1 text-[11px] px-2 py-1"
                    title="Xem chi tiết nguồn nhu cầu và sản phẩm đóng góp"
                  >
                    <GitFork className="h-3 w-3" />
                    Truy vết
                  </Button>
                </td>

                {/* Thuộc tính vật tư */}
                <td className="px-3 py-2.5 text-center">
                  {row.material_id ? (
                    <ContextMenuRowButton
                      onClick={() => {
                        setSelectedMaterialId(row.material_id ?? null)
                        setIsPropsPanelOpen(true)
                      }}
                    />
                  ) : (
                    <span className="text-slate-300 text-[11px]">—</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={{ x: contextMenu.x, y: contextMenu.y }}
        onClose={() => setContextMenu((prev) => ({ ...prev, isOpen: false }))}
        items={[
          {
            id: 'properties',
            label: 'Thuộc tính vật tư...',
            icon: <SlidersHorizontal className="h-4 w-4" />,
            onClick: () => {
              if (contextMenu.row?.material_id) {
                setSelectedMaterialId(contextMenu.row.material_id)
                setIsPropsPanelOpen(true)
              }
            },
          },
          {
            id: 'trace',
            label: 'Truy vết nhu cầu...',
            icon: <GitFork className="h-4 w-4" />,
            onClick: () => {
              if (contextMenu.row) {
                onOpenTrace(contextMenu.row)
              }
            },
          },
        ]}
      />

      {isPropsPanelOpen && (
        <MaterialPropertiesPanel
          isOpen={isPropsPanelOpen}
          materialId={selectedMaterialId}
          onClose={() => {
            setIsPropsPanelOpen(false)
            setSelectedMaterialId(null)
          }}
        />
      )}
    </div>
  )
}
